import { useState } from "react";
import { siteConfig } from "@/config/site";

const FORM_URL = "https://readdy.ai/api/form/dbad4o69if4sq287d0s0";
const MAX_MESSAGE = 500;

interface FormValues {
  name: string;
  email: string;
  topic: string;
  message: string;
}

const initialValues: FormValues = {
  name: "",
  email: "",
  topic: "内容建议",
  message: "",
};

export default function FeedbackForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldError, setFieldError] = useState("");

  const update = (key: keyof FormValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const topic = String(formData.get("topic") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setFieldError("请填写称呼、邮箱与反馈内容后再提交。");
      setStatus("error");
      setErrorMessage("");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldError("请输入有效的邮箱地址。");
      setStatus("error");
      setErrorMessage("");
      return;
    }
    if (message.length > MAX_MESSAGE) {
      setFieldError(`反馈内容不能超过 ${MAX_MESSAGE} 个字符。`);
      setStatus("error");
      setErrorMessage("");
      return;
    }

    setFieldError("");

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("topic", topic);
    payload.append("message", message);

    if (honeypot) {
      setStatus("success");
      setValues(initialValues);
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const isSpam = serverMessage.toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        setValues(initialValues);
        formEl.reset();
      } else {
        setStatus("error");
        setErrorMessage(serverMessage || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setErrorMessage("网络异常，提交未能完成，请稍后重试或直接发邮件给我们。");
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-500 text-primary-950">
          <i className="ri-chat-smile-2-line text-base" aria-hidden="true"></i>
        </span>
        <div>
          <h2 className="text-sm font-medium text-foreground-900">意见反馈</h2>
          <p className="text-[11px] text-foreground-500">内容建议、错误纠正或合作洽谈都可以写在这里</p>
        </div>
      </div>

      {status === "success" ? (
        <div className="mt-5 rounded-md bg-secondary-100 p-5 text-center">
          <i className="ri-checkbox-circle-line text-2xl text-secondary-800" aria-hidden="true"></i>
          <p className="mt-2 text-sm font-medium text-secondary-900">反馈已收到，谢谢你！</p>
          <p className="mt-1 text-xs text-secondary-800">我们会认真阅读每一条留言，必要时通过邮箱与你联系。</p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-4 rounded-md bg-primary-800 px-4 py-2 text-xs text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-700"
          >
            再写一条
          </button>
        </div>
      ) : (
        <form id="feedback-form" data-readdy-form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="feedback-name" className="mb-1.5 block text-xs text-foreground-700">
                你的称呼 <span className="text-accent-600">*</span>
              </label>
              <input
                id="feedback-name"
                name="name"
                type="text"
                value={values.name}
                onChange={(event) => update("name", event.target.value)}
                placeholder="如：小林"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <div>
              <label htmlFor="feedback-email" className="mb-1.5 block text-xs text-foreground-700">
                邮箱 <span className="text-accent-600">*</span>
              </label>
              <input
                id="feedback-email"
                name="email"
                type="email"
                value={values.email}
                onChange={(event) => update("email", event.target.value)}
                placeholder="you@example.com"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
          </div>

          <div>
            <label htmlFor="feedback-topic" className="mb-1.5 block text-xs text-foreground-700">
              反馈类型
            </label>
            <select
              id="feedback-topic"
              name="topic"
              value={values.topic}
              onChange={(event) => update("topic", event.target.value)}
              className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
            >
              <option value="内容建议">内容建议</option>
              <option value="错误纠正">错误纠正</option>
              <option value="功能想法">功能想法</option>
              <option value="合作洽谈">合作洽谈</option>
            </select>
          </div>

          <div>
            <label htmlFor="feedback-message" className="mb-1.5 flex items-center justify-between text-xs text-foreground-700">
              <span>
                反馈内容 <span className="text-accent-600">*</span>
              </span>
              <span className={values.message.length > MAX_MESSAGE ? "text-accent-700" : "text-foreground-400"}>
                {values.message.length} / {MAX_MESSAGE}
              </span>
            </label>
            <textarea
              id="feedback-message"
              name="message"
              rows={5}
              maxLength={MAX_MESSAGE}
              value={values.message}
              onChange={(event) => update("message", event.target.value.slice(0, MAX_MESSAGE))}
              placeholder="说说你的想法，最多 500 字"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            ></textarea>
          </div>

          <div className="honeypot-field" aria-hidden="true">
            <label htmlFor="feedback-contact-alt">请勿填写此栏</label>
            <input
              id="feedback-contact-alt"
              name="contact_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          {status === "error" && (
            <p className="rounded-md bg-accent-100 px-3 py-2 text-xs leading-relaxed text-accent-900">
              {fieldError || errorMessage}
            </p>
          )}

          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] text-foreground-400">提交即表示你同意我们使用该邮箱回复此条反馈。</p>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="inline-flex items-center gap-2 rounded-md bg-primary-800 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap transition-colors cursor-pointer hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-background-200 border-t-transparent"></span>
                  提交中
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line" aria-hidden="true"></i>
                  提交反馈
                </>
              )}
            </button>
          </div>
        </form>
      )}

      <p className="mt-5 border-t border-background-200 pt-4 text-xs text-foreground-500">
        也可以直接发邮件：
        <a href={`mailto:${siteConfig.contactEmail}`} className="ml-1 text-primary-700 hover:underline cursor-pointer">
          {siteConfig.contactEmail}
        </a>
      </p>
    </div>
  );
}